import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listRestaurantApplications,
  getRestaurantApplication,
  approveRestaurantApplication,
  rejectRestaurantApplication,
} from './adminApi';

const STATUS_FILTERS = ['pending', 'approved', 'rejected', 'all'];
const DAY_LABELS = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };

function formatAddress(app) {
  return [app.addressLine1, app.addressLine2, app.city, app.region, app.postalCode, app.country].filter(Boolean).join(', ');
}

function RestaurantDetail({ application, secret, onBack, onDecided }) {
  const [reviewerName, setReviewerName] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleApprove = async () => {
    if (!reviewerName.trim()) { setError('Enter your name as the reviewer first.'); return; }
    setBusy(true); setError(null);
    try {
      await approveRestaurantApplication(application.id, reviewerName.trim(), notes.trim(), secret);
      onDecided();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const handleReject = async () => {
    if (!reviewerName.trim()) { setError('Enter your name as the reviewer first.'); return; }
    if (!notes.trim()) { setError('A rejection reason is required.'); return; }
    setBusy(true); setError(null);
    try {
      await rejectRestaurantApplication(application.id, reviewerName.trim(), notes.trim(), secret);
      onDecided();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const isDecided = application.status === 'approved' || application.status === 'rejected';
  const hasCoords = application.latitude != null && application.longitude != null && !(application.latitude === 0 && application.longitude === 0);
  const weeklyHours = application.weeklyHours || {};

  return (
    <div>
      <button className="adm-back-btn" onClick={onBack}>&larr; Back to queue</button>
      <div className="adm-detail-header">
        <h2 className="adm-detail-name">{application.businessName}</h2>
        <span className={`adm-status-pill adm-status-${application.status}`}>{application.status}</span>
      </div>
      <div className="adm-detail-meta">{application.email} &middot; {application.country} &middot; applied {new Date(application.createdAt).toLocaleString()}</div>

      <div className="adm-detail-grid">
        <div className="adm-detail-card">
          <h4>Business</h4>
          <div className="adm-detail-row"><span>Cuisine</span><span>{application.cuisine || '—'}</span></div>
          <div className="adm-detail-row"><span>Description</span><span style={{ textAlign: 'right', maxWidth: 220 }}>{application.description || '—'}</span></div>
        </div>
        <div className="adm-detail-card">
          <h4>Owner / Contact</h4>
          <div className="adm-detail-row"><span>Name</span><span>{application.ownerName}</span></div>
          <div className="adm-detail-row"><span>Email</span><span>{application.email}</span></div>
          <div className="adm-detail-row"><span>Phone</span><span>{application.phoneNumber || '—'}</span></div>
        </div>
        <div className="adm-detail-card">
          <h4>Location</h4>
          <div className="adm-detail-row"><span>Address</span><span style={{ textAlign: 'right', maxWidth: 220 }}>{formatAddress(application)}</span></div>
          <div className="adm-detail-row">
            <span>Coordinates</span>
            <span>{hasCoords ? `${application.latitude.toFixed(5)}, ${application.longitude.toFixed(5)}` : 'Not captured'}</span>
          </div>
          <div className="adm-detail-row"><span>Timezone</span><span>{application.timezone || '—'}</span></div>
        </div>
      </div>

      <h4 className="adm-section-heading">Weekly Hours</h4>
      <p className="adm-section-note">Exactly what becomes the real Restaurant document's schedule on approval.</p>
      <table className="adm-hours-table">
        <tbody>
          {Object.keys(DAY_LABELS).map((day) => {
            const hours = weeklyHours[day];
            return (
              <tr key={day}>
                <td>{DAY_LABELS[day]}</td>
                <td>{hours?.open && hours?.close ? `${hours.open} – ${hours.close}` : 'Closed'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {!hasCoords && !isDecided && (
        <p className="adm-section-note" style={{ color: '#ffd166', marginTop: 12 }}>
          ⚠ No location was captured (applicant didn't grant browser location access) - the restaurant will be created
          at placeholder coordinates [0, 0]. Confirm the real location manually during onboarding.
        </p>
      )}

      {application.status === 'approved' && (
        <p className="adm-section-note" style={{ marginTop: 12 }}>
          Menu, photos and delivery zones still need to be set up before this restaurant can go live - approval only
          creates the base record and the owner's account (onboardingComplete: false).
        </p>
      )}

      {application.reviewedBy && (
        <div className={`adm-decided-note adm-decided-note--${application.status === 'approved' ? 'approved' : 'rejected'}`}>
          {application.status === 'approved' ? 'Approved' : 'Rejected'} by <strong>{application.reviewedBy}</strong> on {new Date(application.reviewedAt).toLocaleString()}
          {(application.reviewNotes || application.rejectionReason) && <> — "{application.reviewNotes || application.rejectionReason}"</>}
          {application.restaurantId && <> — Restaurant ID: <code>{application.restaurantId}</code></>}
        </div>
      )}

      {!isDecided && (
        <div className="adm-review-box">
          <h4>Reviewer Decision</h4>
          <input className="adm-review-input" placeholder="Your name (reviewer)" value={reviewerName} onChange={(e) => setReviewerName(e.target.value)} />
          <textarea className="adm-review-input" style={{ height: 70, resize: 'vertical' }} placeholder="Notes (required if rejecting)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          {error && <div className="adm-error-banner">{error}</div>}
          <div className="adm-review-actions">
            <button className="adm-approve-btn" onClick={handleApprove} disabled={busy}>{busy ? 'Working…' : 'Approve & Create Account'}</button>
            <button className="adm-reject-btn" onClick={handleReject} disabled={busy}>{busy ? 'Working…' : 'Reject'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function RestaurantApplicationsPanel({ secret }) {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [applications, setApplications] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);

  const loadQueue = useCallback(() => {
    setLoading(true); setError(null);
    listRestaurantApplications(statusFilter, secret)
      .then(setApplications)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [statusFilter, secret]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  useEffect(() => {
    if (!selectedId) { setSelectedApplication(null); return; }
    getRestaurantApplication(selectedId, secret).then(setSelectedApplication).catch((err) => setError(err.message));
  }, [selectedId, secret]);

  const handleDecided = () => { setSelectedId(null); setSelectedApplication(null); loadQueue(); };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return applications;
    return applications.filter((a) =>
      a.businessName?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q) ||
      a.city?.toLowerCase().includes(q) || a.country?.toLowerCase().includes(q)
    );
  }, [applications, search]);

  if (selectedApplication) {
    return <RestaurantDetail application={selectedApplication} secret={secret} onBack={() => setSelectedId(null)} onDecided={handleDecided} />;
  }

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">Restaurant Applications</h1>
          <p className="adm-page-subtitle">
            Approving creates a real Restaurant record and a restaurant_owner account, and emails the applicant a
            link to set their password. Menu, photos and delivery zones still need a follow-up onboarding pass.
          </p>
        </div>
        <button className="adm-refresh-btn" onClick={loadQueue} disabled={loading}>{loading ? 'Loading…' : '⟳ Refresh'}</button>
      </div>

      <div className="adm-toolbar">
        <div className="adm-filter-pills">
          {STATUS_FILTERS.map((s) => (
            <button key={s} className={`adm-filter-pill${s === statusFilter ? ' adm-filter-pill-active' : ''}`} onClick={() => setStatusFilter(s)}>
              {s}
            </button>
          ))}
        </div>
        <input className="adm-search-input" placeholder="Search name, email, city…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {error && <div className="adm-error-banner">{error}</div>}

      {filtered.length === 0 ? (
        <div className="adm-empty-state">No applications match this view.</div>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Business</th><th>Owner</th><th>Cuisine</th><th>City</th><th>Country</th><th>Status</th><th>Submitted</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((app) => (
                <tr key={app.id}>
                  <td>{app.businessName}</td>
                  <td>{app.ownerName}</td>
                  <td>{app.cuisine || '—'}</td>
                  <td>{app.city}</td>
                  <td>{app.country}</td>
                  <td><span className={`adm-status-pill adm-status-${app.status}`}>{app.status}</span></td>
                  <td>{new Date(app.createdAt).toLocaleDateString()}</td>
                  <td><button className="adm-review-btn" onClick={() => setSelectedId(app.id)}>Review</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
